import { type FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingIcon } from "@App/Components/common/Icons";
import { useDispatch, useSelector } from "react-redux";
import { type State } from "@App/state";
import type { DriverDataApi } from "@App/apis/rest/ayvens/driverData/interfaces";
import type { VehicleData } from "@App/apis/rest/ayvens/driverData/responses/fleetManagerSuccessResponse";
import type { DriverDataError } from "@App/apis/rest/ayvens/driverData/driverDataError";
import { List } from "@App/Components/ClientSpecific/Ayvens/VehicleList/List";
import { Pagination } from "@App/Components/ClientSpecific/Ayvens/VehicleList/Pagination";
import { Role } from "@App/types/ayvens/role";
import { useVehicleSelector } from "@App/Pages/Partner/Ayvens/BusinessLogic/backToVehicleSelector";
import { setFleetManagerData } from "@App/state/reducers/auth";

export type FleetManagerVehicleSelectorProps = {
  driverDataApi: DriverDataApi;
  onSubmit: (token: string) => void;
};

export const FleetManagerVehicleSelector: FunctionComponent<FleetManagerVehicleSelectorProps> = ({
  driverDataApi,
  onSubmit,
}: FleetManagerVehicleSelectorProps) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [error, setError] = useState<Error | null>(null);

  const initialToken = useSelector((state: State) => state.auth.initialToken);

  const dispatch = useDispatch();
  const fleetManagerData = useSelector((state: State) => state.auth.fleetManagerData);
  const { resetTokenAndDriverData } = useVehicleSelector();

  // on browser-back, the token and driver data should be reset
  useEffect(() => {
    resetTokenAndDriverData();
  }, []);

  const fetchVehicleList = async (token: string, currentPage: number): Promise<void> => {
    try {
      const response = await driverDataApi.getVehicleListFromJwt(token, currentPage);
      setError(null);
      dispatch(setFleetManagerData(response));
    } catch (error) {
      setError(error);
      console.error("Error fetching vehicle list:", error);
    }
  };

  useEffect(() => {
    if (!initialToken) {
      return;
    }

    fetchVehicleList(initialToken, currentPage);
  }, [initialToken, currentPage]);

  const previousPage = () => {
    currentPage > 1 && setCurrentPage(currentPage - 1);
  };

  const nextPage = () => {
    fleetManagerData && currentPage < fleetManagerData.last_page && setCurrentPage(currentPage + 1);
  };

  const goToPage = (page: number) => {
    fleetManagerData && page >= 1 && page <= fleetManagerData.last_page && setCurrentPage(page);
  };

  const onSelect = async (vehicle: VehicleData) => {
    // reset the booking process (if it's not the first booking) and let the fleet manager book like a driver
    resetTokenAndDriverData(Role.DRIVER);
    onSubmit(vehicle.token);
  };

  return (
    <div className={"min-h-screen"}>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 mb-3 text-center text-2xl/9 font-bold tracking-tight text-gray-900 font-serif">
            {t("vehicleList.title")}
          </h2>
          <p>{t("vehicleList.description")}</p>
        </div>
        <div className="mt-10 sm:mx-auto max-w-md sm:w-full">
          {!error && fleetManagerData === null && (
            <div className="text-center text-gray-500">
              <LoadingIcon className="mx-auto h-12 w-12 text-primary animate-spin" />
            </div>
          )}
          {fleetManagerData && fleetManagerData.data.length === 0 && (
            <div className="text-center text-gray-500">{t("vehicleList.noVehiclesFound")}</div>
          )}
          {error && (
            <div className="text-center text-gray-500">
              {t(`vehicleList.errors.${(error as DriverDataError).response.name}`)}
            </div>
          )}
          {fleetManagerData && fleetManagerData.data.length > 0 && (
            <>
              <List items={fleetManagerData.data} onSelect={onSelect} />
              <Pagination
                currentPage={currentPage}
                previousPage={previousPage}
                nextPage={nextPage}
                goToPage={goToPage}
                itemsPerPage={fleetManagerData.per_page}
                lastPage={fleetManagerData.last_page}
                totalCount={fleetManagerData.total}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
